import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useReveal } from "@/hooks/useReveal";

/**
 * Senior counsel. Single firm.
 * — An organic, floating cluster of monochrome headshots. Each portrait
 *   drifts independently and periodically cross-fades to another lawyer
 *   from the roster, so the cluster feels alive.
 */

// Fixed organic positions across a 5/3 aspect container.
// (x%, y% from top-left; size class controls width via CSS).
const POSITIONS = [
  { x:  3, y: 16, s: "l"  },
  { x: 16, y: 60, s: "m"  },
  { x: 27, y:  6, s: "xl" },
  { x: 38, y: 40, s: "l"  },
  { x: 47, y: 74, s: "m"  },
  { x: 58, y: 18, s: "l"  },
  { x: 64, y: 56, s: "m"  },
  { x: 74, y:  4, s: "l"  },
  { x: 81, y: 68, s: "xl" },
  { x: 13, y: 34, s: "m"  },
  { x: 32, y: 82, s: "s"  },
  { x: 52, y:  0, s: "s"  },
  { x: 91, y: 34, s: "s"  },
  { x:  1, y: 78, s: "m"  },
];

function CloudSlot({ pos, lawyers, seedIdx, driftIndex }) {
  const initial = seedIdx % Math.max(lawyers.length, 1);
  // Two layers cross-fade between each other to avoid any flash on swap.
  const [layers, setLayers] = useState({ a: initial, b: null });
  const [activeKey, setActiveKey] = useState("a");
  const timerRef = useRef(null);

  useEffect(() => {
    if (lawyers.length < 2) return undefined;

    const pickDifferent = (current) => {
      let next = current;
      let tries = 0;
      while (next === current && tries < 6) {
        next = Math.floor(Math.random() * lawyers.length);
        tries += 1;
      }
      return next;
    };

    const cycle = () => {
      setActiveKey((curKey) => {
        const nextKey = curKey === "a" ? "b" : "a";
        const currentIdx = layersRef.current[curKey];
        const nextIdx = pickDifferent(currentIdx);
        // Set the *incoming* layer's index first, then flip active so the
        // CSS opacity transition runs smoothly over both layers at once.
        layersRef.current = { ...layersRef.current, [nextKey]: nextIdx };
        setLayers((prev) => ({ ...prev, [nextKey]: nextIdx }));
        return nextKey;
      });
      timerRef.current = setTimeout(cycle, 6000 + Math.random() * 4500);
    };

    // first swap is staggered
    timerRef.current = setTimeout(cycle, 3500 + seedIdx * 700 + Math.random() * 1200);
    return () => timerRef.current && clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lawyers]);

  const layersRef = useRef({ a: initial, b: null });
  layersRef.current = layers;

  if (!lawyers.length) return null;
  const lawyerA = layers.a != null ? lawyers[layers.a % lawyers.length] : null;
  const lawyerB = layers.b != null ? lawyers[layers.b % lawyers.length] : null;
  const labelLawyer = (activeKey === "a" ? lawyerA : lawyerB) || lawyerA;

  return (
    <Link
      to={`/people/${labelLawyer.slug}`}
      className={`cloud-slot cloud-slot-${pos.s}`}
      style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
      aria-label={labelLawyer.name}
      data-testid={`featured-lawyer-${labelLawyer.slug}`}
    >
      <span
        className="cloud-drift"
        style={{
          animationDelay: `${(driftIndex * 0.7).toFixed(2)}s`,
          animationDuration: `${16 + (driftIndex % 5) * 2.5}s`,
        }}
      >
        <span className={`cloud-photo cloud-photo-layer ${activeKey === "a" ? "is-on" : "is-off"}`}>
          {lawyerA && <img src={lawyerA.photo_url} alt="" loading="lazy" />}
        </span>
        <span className={`cloud-photo cloud-photo-layer ${activeKey === "b" ? "is-on" : "is-off"}`}>
          {lawyerB && <img src={lawyerB.photo_url} alt="" loading="lazy" />}
        </span>
      </span>
      <span className="cloud-name">
        <span className="cloud-name-inner">
          <span className="block font-medium leading-tight">{labelLawyer.name}</span>
          {(labelLawyer.department?.title || labelLawyer.title) && (
            <span className="block text-[10px] uppercase tracking-[0.14em] text-graphite/55 mt-0.5">
              {labelLawyer.department?.title || labelLawyer.title}
            </span>
          )}
        </span>
      </span>
    </Link>
  );
}

export default function FeaturedLawyers() {
  const [lawyers, setLawyers] = useState([]);
  const ref = useReveal();

  useEffect(() => {
    api.get("/lawyers")
      .then((r) => setLawyers((r.data || []).filter((l) => !!l.photo_url)))
      .catch(() => setLawyers([]));
  }, []);

  // Pre-shuffle once so initial seeds vary
  const seedOrder = useMemo(() => {
    const arr = lawyers.map((_, i) => i);
    for (let i = arr.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [lawyers]);

  return (
    <section className="py-24 md:py-36 reveal" ref={ref} data-testid="featured-lawyers">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12 md:mb-16">
          <div>
            <h2 className="h-display text-[clamp(2.2rem,5vw,4.4rem)] leading-[1.02]">
              Senior counsel.<br />Single firm.
            </h2>
            <p className="mt-6 max-w-md text-graphite/75 leading-relaxed">
              Senior, commercial, and deliberately compact. Meet the people behind every matter.
            </p>
          </div>
          <Link to="/people" className="cec-btn cec-btn-primary self-start md:self-auto" data-testid="featured-lawyers-view-all">
            View the team <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="cloud-canvas" data-testid="featured-lawyers-cloud">
          {lawyers.length > 0 && POSITIONS.map((pos, i) => (
            <CloudSlot
              key={i}
              pos={pos}
              lawyers={lawyers}
              seedIdx={seedOrder[i % seedOrder.length] ?? i}
              driftIndex={i}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
